import { AgentAdmin } from '@innovasium-2014/expanse-embed-agent-panel'
import { Box, CircularProgress, Typography } from '@mui/material'
import { useState, useEffect, useRef } from 'react'
import { connect } from 'react-redux'

const AgentAdminPage = () => {
  const [token, setToken] = useState(null)
  const [error, setError] = useState(null)
  const [iframeLoaded, setIframeLoaded] = useState(false)
  const containerRef = useRef(null)

  // Fire a preconnect + cold-start ping at the Expanse origin immediately, in
  // parallel with the token fetch below. The iframe can't mount until the token
  // arrives, so any TLS handshake / Lambda boot we can finish while the token
  // is in flight shaves directly off perceived load time.
  useEffect(() => {
    const embedUrl = process.env.REACT_APP_INNO_AI_EMBED_URL
    if (!embedUrl) return
    let origin
    try {
      origin = new URL(embedUrl).origin
    } catch {
      return
    }

    const preconnect = document.createElement('link')
    preconnect.rel = 'preconnect'
    preconnect.href = origin
    preconnect.crossOrigin = 'anonymous'
    document.head.appendChild(preconnect)

    // Lightweight no-cors ping to wake the Next.js server lambda. We don't care
    // about the response; opaque is fine.
    fetch(`${origin}/api/embed/warmup`, {
      method: 'GET',
      mode: 'no-cors',
      credentials: 'omit',
      cache: 'no-store',
    }).catch(() => {})

    return () => {
      if (preconnect.parentNode) preconnect.parentNode.removeChild(preconnect)
    }
  }, [])

  // Remove .pageContent side/bottom padding so the iframe is flush with the container edges
  useEffect(() => {
    const pageContent = document.querySelector('.pageContent')
    if (pageContent) {
      pageContent.style.paddingLeft = '0'
      pageContent.style.paddingRight = '0'
      pageContent.style.paddingBottom = '0'
      pageContent.style.overflow = 'hidden'
    }
    return () => {
      if (pageContent) {
        pageContent.style.paddingLeft = ''
        pageContent.style.paddingRight = ''
        pageContent.style.paddingBottom = ''
        pageContent.style.overflow = ''
      }
    }
  }, [])

  useEffect(() => {
    // Mirror the apollo.js URL pattern: when REACT_APP_BACKEND_URL is set
    // (local dev) hit the bare path directly on the backend; otherwise go
    // through nginx, which forwards `/api/*` to the server.
    const url = process.env.REACT_APP_BACKEND_URL
      ? `${process.env.REACT_APP_BACKEND_URL}/admin-embed-token`
      : `${window.location.origin}/api/admin-embed-token`
    fetch(url, { credentials: 'include' })
      .then((res) => {
        if (!res.ok) throw new Error(`Status ${res.status}`)
        return res.json()
      })
      .then((data) => setToken(data.token))
      .catch((err) => {
        console.error('[AgentAdmin] failed to get embed token', err)
        setError('Unable to load admin panel.')
      })
  }, [])

  // Hide the loading overlay once the iframe finishes loading
  useEffect(() => {
    if (!token || !containerRef.current) return

    const iframe = containerRef.current.querySelector('iframe')
    if (!iframe) return

    const handleLoad = () => setIframeLoaded(true)
    iframe.addEventListener('load', handleLoad)
    return () => iframe.removeEventListener('load', handleLoad)
  }, [token])

  if (error) {
    return (
      <div style={{ padding: 24 }}>
        <p>{error}</p>
      </div>
    )
  }

  if (!token) {
    return (
      <Box
        sx={{
          width: '100%',
          height: 'calc(100vh - 100px)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Loading admin panel...
        </Typography>
      </Box>
    )
  }

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        height: 'calc(100vh - 100px)',
        position: 'relative',
      }}
    >
      {!iframeLoaded && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 2,
            zIndex: 1,
            bgcolor: 'background.default',
          }}
        >
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Loading admin panel...
          </Typography>
        </Box>
      )}
      <AgentAdmin
        publicKey={process.env.REACT_APP_INNO_AI_API_KEY}
        token={token}
        baseUrl={process.env.REACT_APP_INNO_AI_EMBED_URL}
        width="100%"
        height="100%"
        onError={(err) => console.error('[AgentAdmin] embed error:', err)}
      />
    </div>
  )
}

function mapStateToProps(state) {
  return {
    userStore: state.UserStore,
  }
}

export default connect(mapStateToProps)(AgentAdminPage)
